import * as React from 'react';
import styled from 'styled-components';
import { useTranslation } from '@blocs.i18n';
import { gqlProfileStatusEnum as PROFILE_STATUS } from '@minecraft.graphql-types';
import CloudinaryPhoto from '@minecraft.cloudinary-photo';
import { getOldestProfile, truncateAndAddEllipsis } from 'utils/helpers';

/* STYLES */
const PhotoWrapper = styled.div`
  display: inline-block;
  margin-right: 1rem;
`;

const StageName = styled.span`
  vertical-align: middle;
  display: inline-block;
  text-align: left;
`;

const BoldStageName = styled(StageName)`
  font-weight: 700;
`;

const InfoLine = styled.span`
  vertical-align: middle;
  display: block;
  font-weight: 400;
`;

type profileCounts = {
  active: number;
  inactive: number;
};

const countProfiles = (profiles): profileCounts => {
  return profiles.reduce(
    (acc, profile) => {
      if (profile.profileStatus.code === PROFILE_STATUS.ACTIVE) {
        acc.active += 1;
      } else {
        acc.inactive += 1;
      }

      return acc;
    },
    {
      active: 0,
      inactive: 0,
    }
  );
};

/* MAIN  */
const ArtistsInfo = ({ artist, showInfo = false, maxLength = null }) => {
  const { t } = useTranslation();
  const profileStatuses = countProfiles(artist.profiles);
  const profile = getOldestProfile(artist);

  const photo = profile && profile.photos && profile.photos[0];
  const stageName = truncateAndAddEllipsis(`${artist.firstName} ${artist.lastName}`, maxLength);
  const hasActive = profileStatuses.active > 0;
  const hasInactive = profileStatuses.inactive > 0;
  let infoMessage = '';

  if (hasActive && hasInactive) {
    infoMessage = t('talent:artistsInfo.profileStatusesBoth', {
      activeCount: profileStatuses.active,
      inactiveCount: profileStatuses.inactive,
    });
  } else if (hasActive) {
    infoMessage = t('talent:artistsInfo.profileStatusesActiveOnly', { activeCount: profileStatuses.active });
  } else if (hasInactive) {
    infoMessage = t('talent:artistsInfo.profileStatusesInactiveOnly', { inactiveCount: profileStatuses.inactive });
  }

  return (
    <>
      <PhotoWrapper>
        <CloudinaryPhoto
          guid={photo?.media?.guid}
          type="xxsm"
          fileKey={photo?.media?.fileKey}
          showThumbnail
          thumbnail={photo?.media?.thumbnail}
          mediaStorageStatus={photo?.media?.mediaStorageStatus}
        />
      </PhotoWrapper>
      {showInfo ? (
        <BoldStageName data-qa-id="artist-info-stage-name">
          {stageName}{' '}
          {showInfo && Number(profileStatuses.active) + Number(profileStatuses.inactive) > 0 && (
            <InfoLine data-qa-id="artist-info-profiles-count">{infoMessage}</InfoLine>
          )}
        </BoldStageName>
      ) : (
        <StageName data-qa-id="artist-info-stage-name">{stageName}</StageName>
      )}
    </>
  );
};

export default ArtistsInfo;
